/**
 * VHAKACHA REVIEWS  (web app)
 * ============================================================
 * Reading and writing traveller reviews.
 *
 * WHAT THIS REPLACED
 * ------------------
 * `StarRating` rendered `generateRandomNumber(5)` — a different score on every
 * render, for every listing, signifying nothing. The stars were decoration.
 *
 * THE RULES DO THE ENFORCING, NOT THIS FILE
 * -----------------------------------------
 * A review may only be written by someone who REDEEMED the booking it is
 * keyed by. That is checked in firestore.rules, which reads the booking and
 * compares its uid and status. Nothing here is a security boundary — the
 * checks below exist so a traveller is told why the button is absent, rather
 * than shown one that fails.
 *
 * ONE BOOKING, ONE REVIEW
 * -----------------------
 * The document id IS the booking id. Uniqueness is structural rather than
 * enforced: writing again edits the same document, so there is no way to
 * review the same stay twice, and no query needed to find out.
 *
 * The average shown on a listing is NOT computed here. It is written to
 * `listings.ratingAverage` by the onReviewWritten trigger in
 * vhakacha-functions, which is the only writer firestore.rules permits — a
 * provider cannot set their own score.
 * ============================================================
 */

import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from 'firebase/firestore';
import { db } from '../config/firebase';

export const REVIEWS_COLLECTION = 'reviews';

/** Longest comment the rules accept. Kept identical to firestore.rules. */
export const MAX_COMMENT_LENGTH = 1000;

/**
 * Whether this booking can be reviewed by the person looking at it.
 *
 * REDEEMED only. A confirmed booking is a promise; the stay has not happened,
 * and a review of it would say nothing. Mirrors the rules rather than adding
 * to them.
 */
export function canReview(booking, uid) {
  if (!booking || !uid) return false;
  return booking.uid === uid && booking.status === 'REDEEMED';
}

/** Clamp to the 1–5 integer the rules accept, so a stray value cannot be sent. */
export function normalizeRating(value) {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return null;
  if (n < 1 || n > 5) return null;
  return n;
}

/**
 * Write or replace the review for a booking.
 *
 * `serviceProviderId` and `listingId` come from the BOOKING, never from a
 * caller's argument — the rules cross-check them against the booking document
 * and would reject a mismatch, so taking them from anywhere else just produces
 * a confusing failure.
 */
export async function submitReview({ booking, uid, authorName, rating, comment }) {
  const score = normalizeRating(rating);
  if (!score) throw new Error('Please choose a rating between 1 and 5.');
  if (!canReview(booking, uid)) {
    throw new Error('You can only review a trip you have completed.');
  }

  const text = String(comment ?? '').trim().slice(0, MAX_COMMENT_LENGTH);
  const existing = await getDoc(doc(db, REVIEWS_COLLECTION, booking.id));

  const payload = {
    bookingId: booking.id,
    listingId: booking.listingId,
    serviceProviderId: booking.serviceProviderId,
    uid,
    authorName: authorName || null,
    rating: score,
    comment: text,
    // `hidden` is a moderation state. It is set false on create and never
    // touched on edit — the rules refuse an author changing it, so sending it
    // in an update would fail the whole write.
    ...(existing.exists() ? {} : { hidden: false, createdAt: new Date().toISOString() }),
    updatedAt: new Date().toISOString(),
  };

  // merge, so an edit does not drop createdAt or a moderator's `hidden`.
  await setDoc(doc(db, REVIEWS_COLLECTION, booking.id), payload, { merge: true });
  return payload;
}

/** The caller's own review of a booking, or null. */
export async function getMyReview(bookingId) {
  if (!bookingId) return null;
  const snap = await getDoc(doc(db, REVIEWS_COLLECTION, bookingId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/** Withdraw a review. The rules allow only its author (or an admin). */
export async function deleteReview(bookingId) {
  await deleteDoc(doc(db, REVIEWS_COLLECTION, bookingId));
}

/**
 * Visible reviews for a listing, newest first.
 *
 * Hidden ones are filtered HERE rather than in the query. A `where('hidden',
 * '==', false)` would silently drop every review written before the field
 * existed — Firestore omits documents missing a filtered field rather than
 * treating them as false — and would need its own composite index.
 */
export async function getListingReviews(listingId, max = 20) {
  if (!listingId) return [];
  try {
    const snapshot = await getDocs(
      query(
        collection(db, REVIEWS_COLLECTION),
        where('listingId', '==', listingId),
        orderBy('createdAt', 'desc'),
        limit(max)
      )
    );
    return snapshot.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((r) => r.hidden !== true);
  } catch (error) {
    // A listing page that cannot load reviews should still show the listing.
    console.error('Failed to load reviews for listing', listingId, error);
    return [];
  }
}

/**
 * What to show beside the stars.
 *
 * Returns null when nothing has been reviewed, so callers can say "No reviews
 * yet" rather than render a confident 0.0 — which reads as a terrible listing
 * instead of a new one.
 */
export function summarizeRating(listing) {
  const count = Number(listing?.ratingCount) || 0;
  if (count <= 0) return null;
  const average = Number(listing?.ratingAverage) || 0;
  return { average, count };
}
